import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_TRANSFER_NOTE, serializeBlocksToHtml, type TemplateDef, type TemplateRenderData } from "@juro/shared";
import { getDocTypeLabel, templatesApi, type DocType } from "../api.js";
import { previewMockValues } from "../lib/previewMockValues.js";
import { Button } from "../components/ui/Button.js";
import { toast } from "../components/Toast.js";
import { useScreenView } from "../lib/analytics.js";

type Preset = { key: string; doc_type: string; label: string; name: string; template: TemplateDef };

// A4 縦を 96dpi で表示したときの実寸（px）。scale の基準にする
const A4_W = 794;
const A4_H = 1123;
const PREVIEW_H = 300;

/**
 * プリセット1件のプレビュー。
 * html-renderer（PDFと同一レンダラ）でモック値を流し込んだ HTML を srcDoc 表示し、
 * カード幅に合わせて縮小する。サーバー往復なし。
 */
function PresetPreview({ preset }: { preset: Preset }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.4);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / A4_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const html = useMemo(
    () =>
      serializeBlocksToHtml(preset.template, {
        ...previewMockValues,
        notes: DEFAULT_TRANSFER_NOTE,
      } as TemplateRenderData),
    [preset.template],
  );

  return (
    <div
      ref={wrapRef}
      style={{
        height: PREVIEW_H,
        background: "#f1f5f9",
        borderBottom: "1px solid #e2e8f0",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <iframe
        srcDoc={html}
        title={`${preset.name} プレビュー`}
        scrolling="no"
        style={{
          border: "none",
          width: A4_W,
          height: A4_H,
          background: "#fff",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          pointerEvents: "none",
          display: "block",
        }}
      />
    </div>
  );
}

export function TemplateLibraryPage() {
  useScreenView("template_library");
  const [searchParams] = useSearchParams();
  const docType = (searchParams.get("type") ?? "") as DocType | "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [installing, setInstalling] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["template-presets"],
    queryFn: () => templatesApi.listPresets(),
  });

  const allPresets = (data?.data ?? []) as Preset[];
  // 指定書類種別を先頭に、その他の種別も選べるように全件表示する
  const presets = docType
    ? [...allPresets.filter((p) => p.doc_type === docType), ...allPresets.filter((p) => p.doc_type !== docType)]
    : allPresets;

  const installMut = useMutation({
    mutationFn: (key: string) => templatesApi.installPreset(key),
    onMutate: (key) => setInstalling(key),
    onSuccess: (tmpl) => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      toast("テンプレートを作成しました。エディタで調整できます");
      navigate(`/settings/templates/${tmpl.id}`);
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : "テンプレートの作成に失敗しました", "error");
    },
    onSettled: () => setInstalling(null),
  });

  return (
    <div style={{ padding: 24, maxWidth: 1080 }}>
      <div style={{ marginBottom: 20 }}>
        <Link to="/settings/templates" style={{ color: "#0f766e", textDecoration: "none", fontSize: 13, fontWeight: 700 }}>
          テンプレート管理へ戻る
        </Link>
        <h1 style={{ margin: "10px 0 4px", fontSize: 22 }}>ライブラリから選ぶ</h1>
        <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
          サンプルをコピーして自分のテンプレートとして編集できます。
          {docType ? `「${getDocTypeLabel(docType)}」のサンプルを先頭に表示しています。` : ""}
        </p>
      </div>

      {isLoading ? (
        <p style={{ color: "#64748b", fontSize: 13 }}>読み込み中...</p>
      ) : presets.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 28 }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>サンプルなし</h2>
          <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 13 }}>
            利用できるサンプルテンプレートはまだありません。
          </p>
          <Link to={`/settings/templates/new${docType ? `?type=${docType}` : ""}`}>
            <Button>テンプレートを新規作成</Button>
          </Link>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {presets.map((p) => (
            <div
              key={p.key}
              style={{
                background: "#fff",
                border: `1px solid ${p.doc_type === docType ? "#5eead4" : "#e2e8f0"}`,
                borderRadius: 8,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <PresetPreview preset={p} />
              <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {getDocTypeLabel(p.doc_type as DocType)}
                  </div>
                </div>
                <Button
                  onClick={() => installMut.mutate(p.key)}
                  disabled={installing !== null}
                >
                  {installing === p.key ? "作成中..." : "このテンプレートを使う"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
